import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, retry, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectRouteBoardId } from '../store/router/router.selectors';

const shouldRetryOnStatuses = new Set([408, 429, 500, 501, 502, 503, 504]);
const maxRetryAttempts = 3;
const scalingDuration = 3000;

/**
 * Configuration for retry mechanism:
 * - maxRetryAttempts: The number of retry attempts before failing completely.
 * - scalingDuration: The base duration for retry delays, which is 10 seconds.
 *
 * The actual delay is determined by multiplying the current retry attempt number
 * with the scalingDuration. For instance, on the first retry, the delay is 3 seconds.
 * On the second retry, the delay is 6 seconds, and so on.
 * The retryConfig object encapsulates this configuration for the retry operation.
 */
const retryConfig = {
  count: maxRetryAttempts,
  delay: (error: HttpErrorResponse, retryAttempt: number) => {
    if (!shouldRetryOnStatuses.has(error.status)) {
      throw error;
    }
    const hasRetryAfterHeader =
      error.status === 429 &&
      error.headers.has('Retry-After') &&
      !isNaN(parseFloat(<string>error.headers.get('Retry-After')));
    const retryDelay = hasRetryAfterHeader
      ? parseFloat(<string>error.headers.get('Retry-After')) * 1000
      : retryAttempt * scalingDuration;
    return timer(retryDelay);
  },
};

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  private boardChanged$ = new Subject<void>();
  private routeBoardId = toSignal(inject(Store).select(selectRouteBoardId));

  constructor() {
    inject(Store).select(selectRouteBoardId).subscribe(() => {
      this.boardChanged$.next(); // Notify subscribers that the board has changed
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Capture the initial board ID to compare later.
    const initialBoardId = this.routeBoardId();

    return next.handle(request).pipe(
      retry({
        count: retryConfig.count,
        delay: (error, retryAttempt) => {
          // Cancel retry if the board changes or if the error is not retryable
          const currentBoardId = this.routeBoardId();
          if (!shouldRetryOnStatuses.has((<HttpErrorResponse>error).status) || currentBoardId !== initialBoardId) {
            console.log(
              `Retry canceled due to board change or non-retryable status. Current board: ${currentBoardId}, Initial board: ${initialBoardId}`
            );
            throw error;
          }
          return retryConfig.delay(error, retryAttempt);
        },
      }),
      takeUntil(this.boardChanged$), // Use takeUntil again here to prevent final errors from propagating if board changes
      catchError((error) => {
        const currentBoardId = this.routeBoardId();
        // Only show the error dialog if the current board ID matches the initial board ID
        if (currentBoardId === initialBoardId) {
          // Trigger the error handling (e.g., show an error modal)
          return throwError(() => error);
        } else {
          // Suppress the error since it was due to board change
          return throwError(() => null);
        }
      })
    );
  }
}
